// src/core/events.ts — Sabit kapasiteli olay havuzu (tahsissiz push/clear) ve olay tip koruyucuları.
// Sim yalnız olay üretir; view/anim/hud/ses her tick sonunda `events.items[0..count)` üzerinden tüketir ve `clear()` çağırır.
import { Ev } from './types.ts';
import type {
  BlockedEvent, DownedEvent, EventList, FellEvent, HitEvent, HitstopEvent, ItemEvent, KnockdownEvent, MatchEvent, ParryEvent, SimEvent, ThrowEvent, WhiffEvent,
} from './types.ts';
import { EVENT_CAP } from '../config/constants.ts';

function blankEvent(): SimEvent {
  return { type: Ev.MoveStart, tick: 0, actor: -1, target: -1, item: -1, move: -1, v0: 0, v1: 0, v2: 0, x: 0, y: 0, z: 0, dx: 0, dz: 0 };
}
function resetEvent(e: SimEvent, type: Ev, tick: number, actor: number, target: number): SimEvent {
  e.type = type; e.tick = tick; e.actor = actor; e.target = target; e.item = -1; e.move = -1;
  e.v0 = 0; e.v1 = 0; e.v2 = 0; e.x = 0; e.y = 0; e.z = 0; e.dx = 0; e.dz = 0;
  return e;
}

/**
 * Havuz: `capacity` olay yaratılışta tahsis edilir; `push` sıradaki nesneyi sıfırlayıp döner (nesne kimliği sabit, hidden class tek).
 * Taşarsa olay DÜŞER: çağırana yine yazılabilir bir "çöp" olay döner (havuza girmez) — sıcak yolda dallanma/istisna yok.
 * `dropped` sayaç test/istatistik içindir.
 */
export class EventPool implements EventList {
  readonly items: SimEvent[];
  count = 0;
  dropped = 0;
  private readonly sink: SimEvent = blankEvent();
  constructor(readonly capacity: number = EVENT_CAP) {
    this.items = new Array<SimEvent>(capacity);
    for (let i = 0; i < capacity; i++) this.items[i] = blankEvent();
  }
  push(type: Ev, tick: number, actor: number, target = -1): SimEvent {
    if (this.count >= this.capacity) { this.dropped++; return resetEvent(this.sink, type, tick, actor, target); }
    const e = this.items[this.count++]!;   // havuz tam dolu yaratıldı; indeks < capacity
    return resetEvent(e, type, tick, actor, target);
  }
  clear(): void { this.count = 0; }
}
export const createEventList = (capacity: number = EVENT_CAP): EventList => new EventPool(capacity);

// ── Tip koruyucuları (AnyEvent birliği yok; tek daraltma yolu bunlar) ──
export const isHit = (e: SimEvent): e is HitEvent => e.type === Ev.Hit;
export const isBlocked = (e: SimEvent): e is BlockedEvent => e.type === Ev.Blocked;
export const isParry = (e: SimEvent): e is ParryEvent => e.type === Ev.Parry;
export const isWhiff = (e: SimEvent): e is WhiffEvent => e.type === Ev.Whiff;
export const isHitstop = (e: SimEvent): e is HitstopEvent => e.type === Ev.Hitstop;
export const isKnockdown = (e: SimEvent): e is KnockdownEvent => e.type === Ev.Knockdown;
export const isThrow = (e: SimEvent): e is ThrowEvent => e.type === Ev.Throw;
export const isDowned = (e: SimEvent): e is DownedEvent => e.type === Ev.Downed;
export const isFell = (e: SimEvent): e is FellEvent => e.type === Ev.Fell;
export const isItem = (e: SimEvent): e is ItemEvent =>
  e.type === Ev.ItemSpawn || e.type === Ev.ItemPickup || e.type === Ev.ItemDrop || e.type === Ev.ItemThrow ||
  e.type === Ev.ItemHit || e.type === Ev.ItemBreak || e.type === Ev.ItemDespawn;
export const isMatch = (e: SimEvent): e is MatchEvent => e.type === Ev.Countdown || e.type === Ev.MatchStart || e.type === Ev.MatchEnd;

/** Olay adı (debug/ HUD günlüğü); sıcak yolda kullanılmaz. */
export const evName = (t: Ev): string => Ev[t] ?? String(t);
